import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styles from './UserProfileDropdown.module.scss';
import { IconChevronDown, IconSettings, IconLogout, IconX } from '@tabler/icons-react';
import { useAuth } from '../../lib/AuthContext';
import { useLogout } from '../../hooks/useAuth';
import { useGetAuthUser } from '../../api/auth-api/auth-api';

interface UserProfileDropdownProps {
  isMinimized?: boolean;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message 
}) => {
  const { t } = useTranslation('navigation');
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <IconX size={18} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <p>{message}</p>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            {t('userDropdown.cancel')}
          </button>
          <button className={styles.confirmButton} onClick={onConfirm}>
            {t('userDropdown.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ isMinimized = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const { t } = useTranslation('navigation');
  const { firstName, lastName, username } = useAuth();
  const logout = useLogout();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { data: userData } = useGetAuthUser();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const displayName = userData?.firstName && userData?.lastName
    ? `${userData.firstName} ${userData.lastName}`
    : firstName && lastName 
    ? `${firstName} ${lastName}`
    : username || 'User';

    const displayEmail = userData?.username || username || 'user@qlario.io';

  const avatarUrl = userData?.avatar || null;
  const initials = `${(userData?.firstName?.[0] ?? '').toUpperCase()}${(userData?.lastName?.[0] ?? '').toUpperCase()}` || (userData?.username?.[0] ?? '?').toUpperCase();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true);
    setIsDropdownOpen(false);
  };

  const handleCloseConfirmation = () => {
    setShowLogoutConfirmation(false);
  };

  const handleConfirmLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setShowLogoutConfirmation(false);
    }
  };  const handleSettings = () => {
    setIsDropdownOpen(false);
    router.navigate({ to: '/user-settings' });
  };

  // If minimized only show just the avatar
  const avatarEl = avatarUrl
    ? <img src={avatarUrl} alt="User Avatar" className={styles.profileAvatar} />
    : <div className={styles.profileAvatarInitials}>{initials}</div>;

  if (isMinimized) {
    return (
      <div className={styles.profileSection}>
        {avatarEl}
      </div>
    );
  }

  return (
    <>
      <div className={styles.profileContainer} ref={dropdownRef}>
        <div 
          className={styles.profileSection} 
          onClick={toggleDropdown}
          role="button"
          tabIndex={0}
          aria-expanded={isDropdownOpen}
          aria-haspopup="menu"
          onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
        >
          {avatarEl}
          <div className={styles.profileDetails}>
            <span className={styles.profileName}>{displayName}</span>
            <span className={styles.profileEmail}>{displayEmail}</span>
          </div>
          <IconChevronDown className={`${styles.profileChevron} ${isDropdownOpen ? styles.profileChevronOpen : ''}`} aria-hidden="true" />
        </div>

        {isDropdownOpen && (
          <div className={styles.dropdownMenu} role="menu">
            <div 
              className={styles.menuItem} 
              onClick={handleSettings}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleSettings()}
            >
              <IconSettings className={styles.menuItemIcon} aria-hidden="true" />
              <span className={styles.menuItemText}>{t('userDropdown.settings')}</span>
            </div>
            
            <div 
              className={`${styles.menuItem} ${styles.logoutItem}`} 
              onClick={handleLogoutClick}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleLogoutClick()}
            >
              <IconLogout className={styles.menuItemIcon} aria-hidden="true" />
              <span className={styles.menuItemText}>{t('userDropdown.logout')}</span>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showLogoutConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmLogout}
        title={t('userDropdown.confirmLogoutTitle')}
        message={t('userDropdown.confirmLogoutMessage')}
      />
    </>
  );
};

export default UserProfileDropdown;
